/**
 * Developer Settings page.
 */

import React, { useEffect, useState } from 'react';
import { getDeveloperSettings, updateDeveloperSettings } from '../services';
import { DeveloperSettings } from '../types';
import {
  Save,
  AlertCircle,
  CheckCircle2,
  Shield,
  Bell,
  User,
  Lock,
} from 'lucide-react';

export default function SettingsPage() {
  const [settings, setSettings] = useState<DeveloperSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [namespace, setNamespace] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    getDeveloperSettings()
      .then((s) => {
        setSettings(s);
        setNamespace(s.namespace);
        setWebsiteUrl(s.websiteUrl);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !settings) return <div className="page-loader">Loading settings</div>;

  const isDirty = namespace !== settings.namespace || websiteUrl !== settings.websiteUrl;

  const handleSave = async () => {
    setSaving(true);
    setSavedOk(false);
    setSaveError('');
    try {
      const updated = await updateDeveloperSettings({ namespace, websiteUrl });
      setSettings(updated);
      setNamespace(updated.namespace);
      setWebsiteUrl(updated.websiteUrl);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p>Manage your account and integration preferences</p>
        </div>
        <button
          className="btn btn-accent"
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          <Save size={16} /> {saving ? 'Saving' : 'Save Changes'}
        </button>
      </div>

      <div className="alert alert-warning" style={{ marginBottom: 24 }}>
        <AlertCircle size={16} />
        <span>
          <strong>Account information</strong> can be edited below. Security, notifications, and billing settings will be configurable in a future update.
        </span>
      </div>

      {savedOk && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          <CheckCircle2 size={16} /> Changes saved successfully.
        </div>
      )}
      {saveError && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          <AlertCircle size={16} /> {saveError}
        </div>
      )}

      {/* â”€â”€ Account Info */}
      <section className="settings-section">
        <div className="settings-section-header">
          <User size={20} />
          <h2>Account Information</h2>
        </div>
        <div className="settings-grid">
          <div className="form-group">
            <label>Namespace</label>
            <input
              value={namespace}
              onChange={(e) => setNamespace(e.target.value)}
              placeholder="Your company name or something unique to identify you"
              maxLength={200}
            />
            <span className="form-hint">Displayed to users as the app they're interacting with.</span>
          </div>
          <div className="form-group">
            <label>
              Contact Email
              <span className="field-locked"><Lock size={11} /> locked</span>
            </label>
            <input
              type="email"
              value={settings.contactEmail}
              disabled
            />
            <span className="form-hint">To change your email, contact support.</span>
          </div>
          <div className="form-group full-width">
            <label>Website (optional)</label>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://yourapp.com"
            />
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="settings-section" style={{ opacity: 0.5, pointerEvents: 'none' }}>
        <div className="settings-section-header">
          <Shield size={20} />
          <h2>Security <span className="coming-soon-tag">Coming soon</span></h2>
        </div>
        <div className="toggle-row">
          <div>
            <strong>Two-Factor Authentication</strong>
            <p className="muted">Add an extra layer of security to your account.</p>
          </div>
          <label className="toggle">
            <input type="checkbox" checked={settings.twoFactorEnabled} disabled />
            <span className="toggle-slider" />
          </label>
        </div>
        <div className="toggle-row">
          <div>
            <strong>IP Allow-Listing</strong>
            <p className="muted">Restrict API access to specific IP addresses (Enterprise only).</p>
          </div>
          <label className="toggle">
            <input type="checkbox" checked={settings.ipAllowListEnabled} disabled />
            <span className="toggle-slider" />
          </label>
        </div>
      </section>

      {/* Notifications */}
      <section className="settings-section" style={{ opacity: 0.5, pointerEvents: 'none' }}>
        <div className="settings-section-header">
          <Bell size={20} />
          <h2>Notifications <span className="coming-soon-tag">Coming soon</span></h2>
        </div>
        {([
          { key: 'usageAlerts' as const, label: 'Usage Alerts', desc: 'Get notified when you approach rate limits.' },
          { key: 'billingAlerts' as const, label: 'Billing Alerts', desc: 'Notifications for invoices and payment issues.' },
          { key: 'securityAlerts' as const, label: 'Security Alerts', desc: 'Alerts for suspicious API key usage or failed auth attempts.' },
          { key: 'weeklyDigest' as const, label: 'Weekly Digest', desc: 'Summary of usage and key metrics each week.' },
        ]).map((n) => (
          <div key={n.key} className="toggle-row">
            <div>
              <strong>{n.label}</strong>
              <p className="muted">{n.desc}</p>
            </div>
            <label className="toggle">
              <input type="checkbox" checked={settings.notifications[n.key]} disabled />
              <span className="toggle-slider" />
            </label>
          </div>
        ))}
      </section>

      {/* Danger Zone */}
      <section className="settings-section danger-zone">
        <h2>Danger Zone</h2>
        <div className="danger-row">
          <div>
            <strong>Delete Account</strong>
            <p className="muted">Permanently delete your developer account and all associated data. This action cannot be undone.</p>
          </div>
          <button className="btn btn-danger" disabled>Delete Account</button>
        </div>
      </section>
    </div>
  );
}
